/**
 * Comprehensive ingredient parser that extracts quantity, unit, and name
 * from ingredient text in a single pass
 */

export interface IngredientPart {
  startPos: number;
  endPos: number;
  value: string;
}

export interface ParsedIngredient {
  quantity?: IngredientPart;
  unit?: IngredientPart;
  name?: IngredientPart;
  raw: string;
}

// Comprehensive measurement units
const MEASUREMENT_UNITS = new Set([
  // Volume
  'cup',
  'cups',
  'tablespoon',
  'tablespoons',
  'teaspoon',
  'teaspoons',
  'tbsp',
  'tsp',
  'fluid ounce',
  'fluid ounces',
  'fl oz',
  'pint',
  'pints',
  'quart',
  'quarts',
  'gallon',
  'gallons',
  'liter',
  'liters',
  'l',
  'milliliter',
  'milliliters',
  'ml',

  // Weight
  'ounce',
  'ounces',
  'oz',
  'pound',
  'pounds',
  'lb',
  'lbs',
  'gram',
  'grams',
  'g',
  'kilogram',
  'kilograms',
  'kg',

  // Count/piece descriptors
  'piece',
  'pieces',
  'slice',
  'slices',
  'clove',
  'cloves',
  'stick',
  'sticks',
  'leaf',
  'leaves',
  'sprig',
  'sprigs',
  'pinch',
  'pinches',
  'dash',
  'dashes',
]);

// Quantity patterns - handles fractions, decimals, mixed numbers, unicode fractions
const QUANTITY_REGEX =
  /^(\d+(?:[¼½¾⅓⅔⅛⅜⅝⅞]|\.\d+|\s+\d+\/\d+|\/\d+)?|[¼½¾⅓⅔⅛⅜⅝⅞]|\d+\/\d+)/;

/**
 * Parse a single ingredient line into quantity, unit, and name components
 */
export function parseIngredient(text: string): ParsedIngredient {
  const trimmed = text.trim();
  const result: ParsedIngredient = { raw: trimmed };

  if (!trimmed) return result;

  let currentPos = 0;

  // Try to extract quantity (first word or two)
  const quantityMatch = trimmed.match(QUANTITY_REGEX);
  if (quantityMatch) {
    const quantityText = quantityMatch[1];
    result.quantity = {
      startPos: currentPos,
      endPos: currentPos + quantityText.length,
      value: quantityText,
    };
    currentPos = result.quantity.endPos;

    // Skip whitespace after quantity
    while (currentPos < trimmed.length && /\s/.test(trimmed[currentPos])) {
      currentPos++;
    }
  }

  // Try to extract unit (next word or words)
  if (currentPos < trimmed.length) {
    const remaining = trimmed.slice(currentPos);
    const remainingWords = remaining.split(/\s+/);

    // Check for unit (can be 1-2 words like "fluid ounce")
    let unitText = '';
    let unitEndPos = currentPos;

    // Try single word unit first
    if (MEASUREMENT_UNITS.has(remainingWords[0]?.toLowerCase())) {
      unitText = remainingWords[0];
      unitEndPos = currentPos + unitText.length;
    }
    // Try two-word unit like "fluid ounce"
    else if (remainingWords.length >= 2) {
      const twoWordUnit =
        `${remainingWords[0]} ${remainingWords[1]}`.toLowerCase();
      if (MEASUREMENT_UNITS.has(twoWordUnit)) {
        unitText = `${remainingWords[0]} ${remainingWords[1]}`;
        unitEndPos = currentPos + unitText.length;
      }
    }

    if (unitText) {
      result.unit = {
        startPos: currentPos,
        endPos: unitEndPos,
        value: unitText,
      };
      currentPos = unitEndPos;

      // Skip whitespace after unit
      while (currentPos < trimmed.length && /\s/.test(trimmed[currentPos])) {
        currentPos++;
      }
    }
  }

  // Extract ingredient name (everything after quantity and unit)
  if (currentPos < trimmed.length) {
    let nameText = trimmed.slice(currentPos);

    // Handle "of" preposition
    if (nameText.toLowerCase().startsWith('of ')) {
      currentPos += 3; // Skip "of "
      nameText = trimmed.slice(currentPos);
    }

    // Clean up the name - remove trailing punctuation and parenthetical notes for the name part
    nameText = nameText
      .replace(/\s*\([^)]*\).*$/, '') // Remove parenthetical and everything after
      .replace(
        /\s*[,.]\s*(room temperature|chopped|diced|sliced|minced|beaten|melted|softened|at room temperature|finely chopped|coarsely chopped|thinly sliced|sifted|granulated|fresh|dried|large|small|medium|boneless|skinless|skin-on|bone-in).*$/i,
        '',
      ) // Remove preparation instructions after comma/period
      .replace(/[,.]$/, '') // Remove trailing comma or period
      .trim();

    if (nameText) {
      result.name = {
        startPos: currentPos,
        endPos: currentPos + nameText.length,
        value: nameText,
      };
    }
  }

  return result;
}

/**
 * Extract ingredient names from multiple lines of ingredient text
 */
export function extractIngredientNamesFromText(text: string): string[] {
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line);
  const ingredients: string[] = [];

  for (const line of lines) {
    const parsed = parseIngredient(line);
    // Only extract ingredients from lines that have quantities (indicating they're actual ingredient lines)
    if (parsed.name && parsed.name.value.length > 1 && parsed.quantity) {
      // Normalize to lowercase for consistency
      const ingredientName = parsed.name.value.toLowerCase().trim();
      if (!ingredients.includes(ingredientName)) {
        ingredients.push(ingredientName);
      }
    }
  }

  return ingredients;
}
